import { type AllSettingsData, type SettingsData, type ClassRecord } from '../../../models/types';
import { DEFAULT_SCHOOL_OPTIONS } from '../config/settingsConfig';
import { type ClassRecordFromApi } from '../context/types';
import {
    type AttendanceSettings,
    type ClassLevelOptions,
    ClassLevelOverrideOptions,
    type ClassOptionsOverrides,
    type FeatureSettings,
    type OverridableClassOptions
} from '../types/adminConfig';
import { type SchoolSettings } from '../types/apiTypes';

export const CLASS_OPTIONS_KEYS: (keyof OverridableClassOptions)[] = [
    'hasAttendance',
    'webExperience',
    'unauthUserAccess',
    'unauthUserText',
    'unenrolledUserAccess',
    'unenrolledUserText',
    'allowVanillaZoom',
    'classJoinPermission',
    'rosterSelfEnrollment',
    'attendanceGradeTardyScore',
    'showAttendanceGrade',
    'attendanceAutoLateThresholdMinutes',
    'hasAiTa',
    'hasFileAnnotations',
    'hasPresentations',
    'allowNextGenMobile',
    'enablePrivateDiscussion',
    'orgScreenshareRes',
    'recordingInitialPermission',
    'recordingsRestrictChatParticipants',
    'hasTelephony',
    'allowStudentJoinPriorMinutes'
];

const defaultClassOptions = CLASS_OPTIONS_KEYS.reduce((acc, option) => {
    acc[option] = DEFAULT_SCHOOL_OPTIONS[option];
    return acc;
}, {}) as OverridableClassOptions;

export const DEFAULT_CLASS_OPTIONS = Object.freeze(defaultClassOptions);

/**
 *
 * @param featureSettings as FeatureSettings or SettingsData object.  We are looking at the options and overrides properties that are the same type.
 * @param classRecord as ClassRecord or ClassRecordFromApi object.  We are looking at the class_options property that is the same type.
 * @returns OverridableClassOptions
 *
 *  Take the featureSettings.options and the classRecord.class_otpions and return a OverridableClassOptions object
 *   - When featureSettings.overrides[option] is set to ClassLevelOverrideOptions.NoOne, the classRecord[option] should be set to the value in featureSettings.options[option]
 *   otherwise the value should be set to the value in classRecord.class_options[option]
 *   - When the classRecord.class_options[option] is undefined the value should be set to the value in featureSettings.options[option]
 *   - When the featureSettings.overrides[option] is required because of the prior two rules, then a default value will be used
 *   - If the featureSetting.options[option] is undefined and needed then the value should be set to the default value in DEFAULT_SCHOOL_OPTIONS
 */
export const getClassOptionSettings = (
    featureSettings: FeatureSettings | SettingsData,
    classRecord: Partial<ClassRecord> | Partial<ClassRecordFromApi>
): OverridableClassOptions => {
    const featureOptions = CLASS_OPTIONS_KEYS.reduce((acc, option) => {
        acc[option] = featureSettings.options[option] ?? DEFAULT_SCHOOL_OPTIONS[option];
        return acc;
    }, {}) as OverridableClassOptions;
    const classOptions = classRecord?.class_options ?? {};
    return CLASS_OPTIONS_KEYS.reduce((acc, option) => {
        const override = featureSettings.overrides[option];
        if (override === ClassLevelOverrideOptions.NoOne) {
            acc[option] = featureOptions[option];
        } else {
            acc[option] = classOptions[option] ?? featureOptions[option];
        }
        return acc;
    }, {}) as OverridableClassOptions;
};

export const mergeAttendanceSettings = (
    adminSettings: SchoolSettings | AllSettingsData,
    classRecord?: Partial<ClassRecord> | Partial<ClassRecordFromApi>
): AttendanceSettings => {
    const classOptions = classRecord ? getClassOptionSettings(adminSettings, classRecord) : adminSettings.options;
    //initialize any missing overrides
    const overrides = CLASS_OPTIONS_KEYS.reduce((acc, option) => {
        acc[option] = adminSettings.overrides[option] ?? ClassLevelOverrideOptions.NoOne;
        return acc;
    }, {}) as ClassOptionsOverrides;

    return {
        hasAttendance: true, // for now setting to true.
        showAttendanceGrade: classOptions.showAttendanceGrade,
        attendanceTabAccess: adminSettings.attendance_tab_access,
        enablePerSessionAttendanceOptions: adminSettings.options.enablePerSessionAttendanceOptions,
        attendanceGradeTardyScore: classOptions.attendanceGradeTardyScore,
        attendanceGradeTardyScorePercent: classOptions.attendanceGradeTardyScore / 100,
        attendanceAutoLateThresholdMinutes: classOptions.attendanceAutoLateThresholdMinutes,
        attendanceAutoLateThresholdMinutesOverride:
            overrides.attendanceAutoLateThresholdMinutes ?? ClassLevelOverrideOptions.NoOne,
        attendanceGradeTardyScoreOverride: overrides.attendanceGradeTardyScore ?? ClassLevelOverrideOptions.NoOne
    };
};

export function mergeClassLevelSettings(
    orgLevelSettings: SettingsData,
    classOptions: ClassLevelOptions
): OverridableClassOptions {
    return getClassOptionSettings(orgLevelSettings, { class_options: classOptions });
}
